import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { EnhancedFAQs } from "@/components/seo/EnhancedFAQs";
import { AuthorBio } from "@/components/content/AuthorBio";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { RelatedResources } from "@/components/seo/RelatedResources";
import { BreadcrumbNavigation } from "@/components/seo/BreadcrumbNavigation";

const NORTHEAST_STATES = [
  { name: 'Connecticut', abbr: 'CT', url: 'connecticut' },
  { name: 'Maine', abbr: 'ME', url: 'maine' },
  { name: 'Massachusetts', abbr: 'MA', url: 'massachusetts' },
  { name: 'New Hampshire', abbr: 'NH', url: 'new-hampshire' },
  { name: 'New Jersey', abbr: 'NJ', url: 'new-jersey' },
  { name: 'New York', abbr: 'NY', url: 'new-york' },
  { name: 'Pennsylvania', abbr: 'PA', url: 'pennsylvania' },
  { name: 'Rhode Island', abbr: 'RI', url: 'rhode-island' },
  { name: 'Vermont', abbr: 'VT', url: 'vermont' }
];

const NORTHEAST_FAQS = [
  {
    question: "Which Northeast states have the most complex PEO compliance requirements?",
    answer: "New York and Massachusetts typically have the most complex employment laws in the Northeast region, including detailed paid family leave requirements, specific notice provisions, and stringent wage and hour regulations. New Jersey also has comprehensive employment protections that PEOs must navigate carefully."
  },
  {
    question: "Do Northeast PEOs specialize in union environments?",
    answer: "Yes, many Northeast PEOs have extensive experience working with unionized workforces, particularly in states like Pennsylvania and New York where union presence is strong. They understand collective bargaining agreements, union reporting requirements, and the nuances of managing both union and non-union employees."
  },
  {
    question: "How do Northeast PEOs handle the region's high cost of benefits?",
    answer: "Northeast PEOs leverage their scale to negotiate better rates in high-cost markets like New York City and Boston. They often provide access to self-funded health plans, regional carrier networks, and benefit designs that help control costs while meeting the expectations of Northeast employees."
  },
  {
    question: "What makes Northeast PEOs different from other regions?",
    answer: "Northeast PEOs typically have deep expertise in complex state regulations, experience with high-density metropolitan areas, strong relationships with regional insurance carriers, and understanding of the region's diverse industry mix from financial services to manufacturing."
  }
];

const RELATED_RESOURCES = [
  {
    title: "Massachusetts PEO Services",
    description: "Complete guide to PEO services in Massachusetts",
    url: "/states/massachusetts",
    type: "state-guide" as const
  },
  {
    title: "New York PEO Providers",
    description: "Find the best PEO companies in New York",
    url: "/states/new-york",
    type: "state-guide" as const
  },
  {
    title: "Healthcare Industry PEOs",
    description: "Specialized PEO services for healthcare companies",
    url: "/industries/healthcare",
    type: "industry-guide" as const
  },
  {
    title: "Technology PEO Services",
    description: "PEO solutions for tech companies",
    url: "/industries/technology",
    type: "industry-guide" as const
  }
];

export const NortheastRegion = () => {
  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title="Northeast PEO Services: Best Professional Employer Organizations in the Northeast"
        description="Find top PEO companies serving the Northeast region. Expert comparison of Professional Employer Organizations in NY, MA, PA, NJ, CT, and all Northeast states. Get matched with the perfect PEO for your business."
        keywords="Northeast PEO, New York PEO, Massachusetts PEO, Pennsylvania PEO, New Jersey PEO, Connecticut PEO, Northeast Professional Employer Organization"
        canonical="https://thepeoapp.com/regions/northeast"
      />
      
      <LocalBusinessSchema
        businessName="The PEO App - Northeast Region"
        serviceType="Professional Employer Organization Services"
        areaServed="Northeast United States"
        description="Professional Employer Organization matching services for businesses in the Northeast region"
        url="https://thepeoapp.com/regions/northeast"
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation 
            items={[
              { label: "Home", href: "/" },
              { label: "States", href: "/states" },
              { label: "Northeast Region", href: "/regions/northeast" }
            ]}
          />
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Northeast PEO Services: Professional Employer Organizations
            </h1>
            
            <div className="prose prose-lg max-w-none mb-12 text-muted-foreground">
              <p className="text-xl leading-relaxed">
                The Northeast region presents unique challenges for businesses, from complex state regulations to high-cost labor markets. Our vetted Professional Employer Organizations specialize in Northeast compliance requirements and understand the region's diverse business landscape.
              </p>
            </div>

            <div className="bg-card p-8 rounded-xl shadow-lg mb-12">
              <h2 className="text-2xl font-bold mb-6">Why Choose a Northeast-Focused PEO?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Regional Expertise</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Deep understanding of state-specific employment laws</li>
                    <li>• Experience with complex union environments</li>
                    <li>• Knowledge of regional benefit market dynamics</li>
                    <li>• Established relationships with local carriers</li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Market Advantages</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Better negotiating power in high-cost markets</li>
                    <li>• Access to regional talent acquisition networks</li>
                    <li>• Understanding of metropolitan area challenges</li>
                    <li>• Specialized industry knowledge for the region</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-6">Northeast States We Serve</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {NORTHEAST_STATES.map((state) => (
                  <Link
                    key={state.abbr}
                    to={`/states/${state.url}`}
                    className="bg-background p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-2 border"
                  >
                    <span className="text-primary font-semibold">{state.abbr}</span>
                    <span className="text-foreground">{state.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-center mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready to Find Your Northeast PEO?</h2>
              <p className="text-muted-foreground mb-6">
                Get matched with PEO providers who understand the Northeast business environment
              </p>
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free PEO Quotes
                </Button>
              </Link>
            </div>

            <RelatedResources 
              title="Related Northeast Resources"
              resources={RELATED_RESOURCES}
            />

            <div className="mt-16">
              <EnhancedFAQs 
                title="Northeast PEO FAQ"
                faqs={NORTHEAST_FAQS}
                showSchema={true}
              />
            </div>

            <div className="mt-16">
              <AuthorBio showFullBio={false} />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};